/**
 * Footer — practice info status + edit link, Privacy link, tool credit,
 * copyright. The "Edit practice info" link dispatches `open-edit-practice`
 * to surface the practice modal in edit mode.
 *
 * Practice status string updates when state.practice changes:
 *   - null         → "Practice info: not set up yet"
 *   - configured   → "Practice info: <practiceLine>"
 */

import { el } from "../dom";
import { MARKETING } from "../marketing";
import type { Action, AppState } from "../state";

import "./footer.css";

export interface FooterDeps {
  dispatch: (action: Action) => void;
}

export interface FooterHandle {
  el: HTMLElement;
  update: (state: AppState) => void;
}

export function createFooter(deps: FooterDeps): FooterHandle {
  const practiceStatus = el("span", { class: "footer__practice-status" });

  const editLink = el(
    "button",
    {
      type: "button",
      class: "footer__edit-link",
      onclick: () => deps.dispatch({ type: "open-edit-practice" }),
    },
    "Edit practice info",
  );

  const linkEls = MARKETING.footer.links.map((link) =>
    el(
      "a",
      {
        href: link.url,
        class: "footer__link",
        target: "_blank",
        rel: "noopener noreferrer",
      },
      link.label,
    ),
  );

  const root = el(
    "footer",
    { class: "footer no-print", role: "contentinfo" },
    el(
      "div",
      { class: "container footer__inner" },
      el("div", { class: "footer__row" }, practiceStatus, editLink),
      el("div", { class: "footer__row footer__row--links" }, ...linkEls),
      el("p", { class: "footer__credit" }, MARKETING.footer.toolCredit),
      el("p", { class: "footer__copyright" }, MARKETING.footer.copyright),
    ),
  );

  function update(state: AppState): void {
    if (state.practice === null) {
      practiceStatus.textContent = "Practice info: not set up yet";
      practiceStatus.classList.add("footer__practice-status--empty");
    } else {
      practiceStatus.textContent = `Practice info: ${state.practice.practiceLine}`;
      practiceStatus.classList.remove("footer__practice-status--empty");
    }
  }

  return { el: root, update };
}
